import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import {
  AllUsersAPI,
  User,
  IResponseUrl,
  FormFields,
  Squadron,
  APISuccessMessage,
  IPendingInvitations,
  PublicUser,
} from '@webtronic-labs/contracts-proflyt';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthApiService {
  constructor(private http: HttpClient) {}

  public getCurrentUserInfo(): Observable<User> {
    return this.http.get<User>(environment.apiUrl + '/users/current');
  }

  public getCurrentProfilePicture(userId: string): Observable<IResponseUrl> {
    return this.http.get<IResponseUrl>(`${environment.apiUrl}/profiles/picture/${userId}`);
  }

  public getUserByID(userID: string): Observable<AllUsersAPI | PublicUser> {
    return this.http.get<AllUsersAPI>(environment.apiUrl + '/users/id/' + userID);
  }

  public createNewUser(payload: User): Observable<User> {
    return this.http.post<User>(environment.apiUrl + '/users', payload);
  }

  public editUser(userID: string, user: User): Observable<User> {
    return this.http.put<User>(environment.apiUrl + '/users/id/' + userID, user);
  }

  public editCurrentUser(
    firstName: string,
    lastName: string,
    phoneNumber: number,
    isBiometricAuthEnabled: boolean
  ): Observable<User> {
    return this.http.patch<User>(environment.apiUrl + '/users/update-current', {
      firstName,
      lastName,
      phoneNumber,
      isBiometricAuthEnabled,
    });
  }

  public updateCurrentUserPhoto(userId: string, file: File): Observable<IResponseUrl> {
    const ingnoreContentType = 'X-Ignore-ContentType';
    const formData = new FormData();
    formData.append(FormFields.file, file);
    const headers: HttpHeaders = new HttpHeaders().set(ingnoreContentType, '');

    return this.http.post<IResponseUrl>(`${environment.apiUrl}/profiles/picture/${userId}`, formData, { headers });
  }

  public deleteUser(userID: string): Observable<{ id: string }> {
    return this.http.delete<{ id: string }>(environment.apiUrl + '/users/id/' + userID);
  }

  public inviteUser(user: User): Observable<User> {
    return this.http.patch<User>(environment.apiUrl + '/users/invite', user);
  }

  public inviteAllUsers(activeSquadron: Squadron): Observable<User[]> {
    return this.http.patch<User[]>(environment.apiUrl + '/users/invite-all', activeSquadron);
  }

  public areUsersWithPendingInvitations(activeSquadron: Squadron): Observable<IPendingInvitations> {
    const params = new HttpParams().set('activeSquadronId', activeSquadron.id);

    return this.http.get<IPendingInvitations>(environment.apiUrl + '/users/are-pending-invitations', { params });
  }

  public setUserFirstLoginTime(): Observable<APISuccessMessage> {
    return this.http.post<APISuccessMessage>(environment.apiUrl + '/users/first-login-time', {});
  }

  public updateUserTimeZone(timeZone: string): Observable<APISuccessMessage> {
    return this.http.post<APISuccessMessage>(`${environment.apiUrl}/users/time-zone-offset`, { timeZone });
  }

  public promotePublicUserToAircrew(user: User): Observable<User> {
    return this.http.post<User>(`${environment.apiUrl}/users/promote-user`, user);
  }

  public firstLoginPasswordChange(newPassword: string): Observable<APISuccessMessage> {
    return this.http.put<APISuccessMessage>(`${environment.apiUrl}/users/first-password-change`, { newPassword });
  }

  public resetAuthUserUnreadNotificationsCount(): Observable<APISuccessMessage> {
    return this.http.get<APISuccessMessage>(environment.apiUrl + '/users/reset-unread-notifications-count');
  }
}
